<template>
  <div class="statistics-view">
    <div class="statistics-content">

      <div class="statistics-section">
        <h2>System Status</h2>
        <div class="status-card">
          <div class="status-item">
            <h3>Database Connection</h3>
            <span :class="{'status-badge': true, 'status-online': dbStatus === 'online', 'status-offline': dbStatus !== 'online'}">
              {{ dbStatus }}
            </span>
          </div>
          <div class="status-item">
            <h3>API Endpoints</h3>
            <span :class="{'status-badge': true, 'status-online': apiStatus === 'online', 'status-offline': apiStatus !== 'online'}">
              {{ apiStatus }}
            </span>
          </div>
          <!--  TODO polling is not an option: instead use hearthbeat, if esp does not submit data for x mins - its offline supposedly
          <div class="status-item">
            <h3>ESP32</h3>
            <span :class="{'status-badge': true, 'status-online': espStatus === 'online', 'status-offline': espStatus !== 'online'}">
              {{ espStatus }}
            </span>
          </div>
          -->
        </div>
      </div>

      <div class="statistics-section">
        <h2>Website Status</h2>
        <div class="metrics-grid">
          <div class="metric-card">
            <h3>Unique Visitors (Today)</h3>
            <div class="metric-value">{{ websiteVisitors }}</div>
          </div>
          <div class="metrics-grid">
            <div class="metric-card">
              <h3>Total Visits (Today)</h3>
              <div class="metric-value">{{ totalVisits }}</div>
            </div>
          </div>
        </div>
        <div class="chart-container">
          <canvas ref="trafficChart"></canvas>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import Chart from 'chart.js/auto';
import { useTrafficStore } from "@/stores/trafficStore";

export default {
  name: 'StatisticsView',
  setup() {
    const websiteVisitors = ref(0);
    const totalVisits = ref(0);
    const dbStatus = ref('unknown');
    const apiStatus = ref('unknown');
    const espStatus = ref('unknown');
    const trafficChart = ref(null);
    let chart = null;
    let statusInterval = null;

    const trafficStore = useTrafficStore();

    const fetchStatus = async () => {
      await trafficStore.setupWebSocket();

      try {
        const api = await fetch('/api/test');
        apiStatus.value = api.ok ? 'online' : 'offline';
      } catch (_) {
        apiStatus.value = 'offline';
      }

      try {
        if (await trafficStore.checkConnection()) {
          dbStatus.value = "online";
        } else {
          dbStatus.value = 'offline';
        }
      } catch (_) {
        dbStatus.value = 'offline';
      }

      /*
      try {
        const res = await fetch('http://192.168.1.130', { method: 'HEAD', mode: 'no-cors' });
        const isReachable = res.ok || res.type === 'opaque';
        espStatus.value = isReachable ? 'online' : 'offline';
      } catch (error) {
        espStatus.value = 'offline';
      }
      */
    };

    const fetchVisitors = async () => {
      try {
        const res = await fetch('/api/visitors/today');
        const data = await res.json();
        websiteVisitors.value = data.reduce((sum, entry) => sum + Number(entry.unique_device_count), 0);

        const res2 = await fetch('/api/visits/today');
        const data2 = await res2.json();
        totalVisits.value = data2.reduce((sum, entry) => sum + Number(entry.visit_count), 0);

        const hourly = Array(24).fill(0);
        data2.forEach(item => {
          const hour = new Date(item.timestamp).getHours();
          hourly[hour] += item.visit_count;
        });

        if (chart) chart.destroy();
        chart = new Chart(trafficChart.value, {
          type: 'line',
          data: {
            labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
            datasets: [{
              label: 'Visits by Hour',
              data: hourly,
              backgroundColor: 'rgba(54, 162, 235, 0.3)', // brighter fill for dark bg
              borderColor: 'rgba(54, 162, 235, 1)',
              borderWidth: 2,
              tension: 0.4,
              fill: true,
              pointRadius: 2,
              pointHoverRadius: 5
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                labels: {
                  color: '#d1d5db',
                  font: {
                    size: 9,
                    family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
                  },
                  usePointStyle: true
                }
              },
              tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                titleColor: '#d1d5db', // blue
                bodyColor: '#e5e7eb',
                borderColor: '#38bdf8',
                borderWidth: 1,
                padding: 8,
                titleFont: { size: 10, weight: '600' },
                bodyFont: { size: 9 }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: {
                  color: 'rgba(255, 255, 255, 0.05)'
                },
                ticks: {
                  color: '#d1d5db',
                  font: {
                    size: 9
                  }
                }
              },
              x: {
                grid: {
                  display: false
                },
                ticks: {
                  color: '#d1d5db',
                  font: {
                    size: 9
                  }
                }
              }
            },
            layout: {
              padding: {
                top: 5,
                right: 5,
                bottom: 5,
                left: 5
              }
            }
          }
        });
      } catch (err) {
        console.error('Error loading traffic data', err);
      }
    };

    onMounted(() => {
      fetchStatus();
      fetchVisitors();
      statusInterval = setInterval(fetchStatus, 5000); // repeat every 5 seconds
    });

    onBeforeUnmount(() => {
      if (statusInterval) {
        clearInterval(statusInterval);
      }
    });

    return {
      websiteVisitors,
      totalVisits,
      dbStatus,
      apiStatus,
      espStatus,
      trafficChart
    };
  }
};
</script>

<style scoped>
.statistics-view {
  padding: 20px;
  font-family: Arial, sans-serif;
  color: #d1d5db; /* light text */
}

h1 {
  margin-bottom: 30px;
  color: #cbd5e1; /* near white for headings */
}

.statistics-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.statistics-section {
  background-color: #2a2a2a; /* darker card background */
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
}

.status-card {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.status-item {
  padding: 15px;
  background-color: #333;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-badge {
  font-size: 0.8rem;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-online {
  background-color: #264d1d;
  color: #8dbc05;
}

.status-offline {
  background-color: #4a1d1d;
  color: #f87171; /* soft red */
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
}

.metric-card {
  padding: 15px;
  background-color: #333;
  border-radius: 6px;
}

.metric-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #a0aec0;
}

.chart-container {
  margin-top: 20px;
  height: 300px;
}

@media (max-width: 768px) {
  .status-card,
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>

